import { motion } from "framer-motion";
import { Reveal } from "./Reveal";

/* ---------- Badge icon SVGs ---------- */
const FlameIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className}>
    <path
      d="M12 2C12 2 6 9 6 14a6 6 0 0 0 12 0c0-5-6-12-6-12Z"
      fill="currentColor"
      opacity="0.9"
    />
    <path
      d="M12 10c0 0-3 3.5-3 6a3 3 0 0 0 6 0c0-2.5-3-6-3-6Z"
      fill="currentColor"
      opacity="0.5"
    />
  </svg>
);

const TrophyIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className}>
    <path d="M7 4h10v6a5 5 0 0 1-10 0V4Z" fill="currentColor" opacity="0.9" />
    <path
      d="M7 6H4a1 1 0 0 0-1 1v1a4 4 0 0 0 4 4h0V6ZM17 6h3a1 1 0 0 1 1 1v1a4 4 0 0 1-4 4h0V6Z"
      fill="currentColor"
      opacity="0.5"
    />
    <rect
      x="10"
      y="14"
      width="4"
      height="3"
      rx="0.5"
      fill="currentColor"
      opacity="0.7"
    />
    <rect
      x="8"
      y="17"
      width="8"
      height="2"
      rx="1"
      fill="currentColor"
      opacity="0.9"
    />
  </svg>
);

const HeartIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className}>
    <path
      d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35Z"
      fill="currentColor"
      opacity="0.9"
    />
  </svg>
);

const StarBadgeIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className}>
    <path
      d="M12 2l2.4 5.2L20 8l-4 3.8 1 5.7L12 14.7 7 17.5l1-5.7L4 8l5.6-.8L12 2Z"
      fill="currentColor"
      opacity="0.9"
    />
    <circle cx="12" cy="10" r="2.5" fill="currentColor" opacity="0.4" />
  </svg>
);
import { Typewriter } from "./Typewriter";

type Brag = {
  label: string;
  value: string;
  sub: string;
  tint: "red" | "yellow" | "navy" | "soft";
  icon: React.ReactNode;
};

const BRAGS: Brag[] = [
  {
    label: "Longest streak",
    value: "142 days",
    sub: "Your top 1% of users",
    tint: "red",
    icon: <FlameIcon className="w-7 h-7" />,
  },
  {
    label: "Personal best",
    value: "+38%",
    sub: "Beat last year by a mile",
    tint: "yellow",
    icon: <TrophyIcon className="w-7 h-7" />,
  },
  {
    label: "Community impact",
    value: "2,400 hrs",
    sub: "Saved across your team",
    tint: "navy",
    icon: <HeartIcon className="w-7 h-7" />,
  },
  {
    label: "Milestone unlocked",
    value: "3rd year",
    sub: "With you since day one",
    tint: "soft",
    icon: <StarBadgeIcon className="w-7 h-7" />,
  },
];

const tintStyles: Record<Brag["tint"], string> = {
  red: "bg-fwd-red text-white",
  yellow: "bg-fwd-yellow text-fwd-ink",
  navy: "bg-fwd-navy text-white",
  soft: "bg-white text-fwd-ink border border-fwd-ink/10",
};

export const CelebrateSection = () => {
  return (
    <section className="bg-fwd-soft py-24 md:py-32 px-8 md:px-16 lg:px-20 relative overflow-hidden">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center">
        {/* Copy */}
        <div>
          <Reveal>
            <span className="text-fwd-red text-xs font-bold tracking-[0.2em] uppercase block mb-5">
              Give them something to brag about
            </span>
          </Reveal>
          <h2 className="font-display text-[clamp(2.2rem,4vw,3.5rem)] font-normal leading-tight text-fwd-ink mb-6">
            <Typewriter
              segments={[
                "Remind them of everything they\u2019ve accomplished.",
              ]}
              startDelay={300}
              cursorClass="bg-fwd-red"
            />
          </h2>
          <Reveal delay={0.2}>
            <p className="text-fwd-mid text-lg leading-relaxed font-light max-w-md">
              Personal bests. Quiet milestones they never noticed. Growth they
              can&rsquo;t ignore. The wins worth forwarding to their boss, their
              team, their feed.
            </p>
          </Reveal>
        </div>

        {/* Brag cards — staggered grid, fade in place on scroll into view */}
        <div className="grid grid-cols-2 auto-rows-fr gap-4 md:gap-5">
          {BRAGS.map((b, i) => (
            <motion.div
              key={b.label}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              className={i % 2 === 0 ? "md:translate-y-4" : ""}
            >
              <motion.div
                whileHover={{
                  scale: 1.05,
                  y: -6,
                  transition: { type: "spring", stiffness: 300, damping: 20 },
                }}
                style={{
                  boxShadow:
                    "0 8px 30px -6px rgba(17,17,24,0.12), 0 20px 50px -12px rgba(17,17,24,0.08)",
                }}
                className={`h-full rounded-lg p-6 md:p-7 relative overflow-hidden cursor-default
                transition-shadow duration-300 hover:[box-shadow:0_14px_40px_-4px_rgba(17,17,24,0.2),0_30px_70px_-16px_rgba(17,17,24,0.14)]
                ${tintStyles[b.tint]} group`}
              >
                {/* Badge icon — top-right, pops on hover */}
                <motion.div
                  className="absolute top-3 right-3 opacity-25 group-hover:opacity-50 transition-opacity duration-300"
                  whileHover={{ rotate: 12, scale: 1.25 }}
                >
                  {b.icon}
                </motion.div>
                <div className="text-[0.65rem] font-bold tracking-[0.15em] uppercase opacity-70 mb-2">
                  {b.label}
                </div>
                <div className="font-display text-3xl md:text-4xl leading-none mb-2">
                  {b.value}
                </div>
                <div className="text-xs opacity-75 leading-snug">{b.sub}</div>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
